import { logger } from '@automata/utils';
import { RefObject, useCallback, useEffect } from 'react';
import { ActionCreators as ReduxUndoActionCreators } from 'redux-undo';

import {
  keyCommandCopy,
  keyCommandCut,
  keyCommandPaste,
  thunkDeleteSelectedNodesEdges,
} from '../features/actions';
import { selectAllNodesAndEdges } from '../features/graph/graphSlice';
import { selectWorkflowJsonToSave } from '../features/workflowJsonSelectors';
import { useAppDispatch, useAppSelector } from '../hooks';
import { isMacOS } from '../utils';

export type KeyboardEventSubset = Pick<
  KeyboardEvent,
  'key' | 'ctrlKey' | 'metaKey' | 'shiftKey' | 'preventDefault'
>;

// TODO: deprecate this in favour of react-flow's built-in "useKeyPress" hook
const useKeyboardShortcuts = (
  inputRef: RefObject<HTMLElement> = { current: document.body }
) => {
  const dispatch = useAppDispatch();
  const workflow = useAppSelector(selectWorkflowJsonToSave);

  const handleKeyDown = useCallback(
    (e: KeyboardEventSubset) => {
      const modifierKeyPressed = isMacOS() ? e.metaKey : e.ctrlKey;

      if (modifierKeyPressed) {
        switch (e.key.toLowerCase()) {
          case 'c': {
            e.preventDefault();
            dispatch(keyCommandCopy());
            break;
          }
          case 'x': {
            e.preventDefault();
            dispatch(keyCommandCut());
            break;
          }
          case 'v': {
            e.preventDefault();
            dispatch(keyCommandPaste());
            break;
          }
          case 'z': {
            if (e.shiftKey) {
              // modifier + shift + z is redo
              e.preventDefault();
              dispatch(ReduxUndoActionCreators.redo());
            } else {
              e.preventDefault();
              dispatch(ReduxUndoActionCreators.undo());
            }
            break;
          }
          case 'y': {
            // modifier + y is also redo
            e.preventDefault();
            dispatch(ReduxUndoActionCreators.redo());
            break;
          }
          case 'a': {
            e.preventDefault();
            dispatch(selectAllNodesAndEdges());
            break;
          }
          case 'o': {
            if (e.shiftKey) {
              logger.log(workflow);
            }
          }
        }
      }

      if (e.key === 'Backspace' || e.key === 'Delete') {
        // NOTE(IL, 2024-04-01): ReactFlow's built-in delete splits up node and edge deletions,
        // which makes undo/redo and other functionality difficult to implement. We need
        // more control, so we'll implement our own delete that functions atomically to remove
        // both nodes and orphaned edges.
        // See `deleteElements`:
        // https://github.com/xyflow/xyflow/blob/8b200b4f25c5e017a86974161b19bf75656d671b/packages/react/src/hooks/useReactFlow.ts#L167
        dispatch(thunkDeleteSelectedNodesEdges());
      }
    },
    [dispatch, workflow]
  );

  useEffect(() => {
    if (!inputRef.current) return;
    const inputRefCurrent = inputRef.current;

    inputRefCurrent.addEventListener('keydown', handleKeyDown);

    return function cleanup() {
      inputRefCurrent.removeEventListener('keydown', handleKeyDown);
    };
  }, [inputRef, handleKeyDown]);
};

export default useKeyboardShortcuts;
